import { BLOCKS, EXTRA_POSITION_OPTIONS } from "../pages/Schedule/config/scheduleConfig";
import { getRoleDefaultTimes, sortAssignments, timeToMinutes } from "./scheduleTimeUtils";
import {
  isAmbulanceSlot,
  isBikeSlot,
  isCartSlot,
} from "./scheduleRoleUtils";
import { isParamedic } from "./scheduleEmployeeUtils";
import {
  FRONT_9_ASSIGNMENTS,
  BACK_9_ASSIGNMENTS,
} from "./scheduleOrderingUtils";
export const EXTRA_SLOTS_FIELD = "__extraSlots";
export const ASSIGNMENTS_FIELD = "__assignments";
export const BLOCKED_FIELD = "__blockedSlots";
export const UNIT_TEXT_FIELD = "__unitText";
export const AVAILABLE_SHIFTS_FIELD = "__availableShifts";

export function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function isLegacyCartBikeBaseKey(roleKey) {
  const key = String(roleKey || "").trim();
  return isCartSlot(key) || isBikeSlot(key);
}

function isCartBikeChildKey(roleKey) {
  return /__(paramedic|partner)$/i.test(String(roleKey || "").trim());
}

function isTentBaseKey(roleKey) {
  return /^Tent\s+\d+$/i.test(String(roleKey || "").trim());
}

function getTentChildKeys(baseRoleKey) {
  return [1, 2, 3, 4].map((n) => `${baseRoleKey}__${n}`);
}

function getCartBikeChildKeys(baseRoleKey) {
  return {
    medicKey: `${baseRoleKey}__paramedic`,
    partnerKey: `${baseRoleKey}__partner`,
  };
}

function getBaseRoleKeyFromChild(roleKey) {
  return String(roleKey || "").replace(/__(paramedic|partner)$/i, "");
}

export function getAddOptionsForBlock(blockTitle) {
  if (blockTitle === "Command Team") {
    return EXTRA_POSITION_OPTIONS.filter(
      (opt) => opt !== "Ambulance (ALS)" && opt !== "Ambulance (BLS)"
    );
  }

  return EXTRA_POSITION_OPTIONS;
}

export function normalizeDay(dayObj) {
  const day = dayObj && typeof dayObj === "object" ? { ...dayObj } : {};
  const next = { ...day };

  if (!next[ASSIGNMENTS_FIELD] || typeof next[ASSIGNMENTS_FIELD] !== "object") {
    next[ASSIGNMENTS_FIELD] = {};
  }
  if (!next[BLOCKED_FIELD] || typeof next[BLOCKED_FIELD] !== "object") {
    next[BLOCKED_FIELD] = {};
  }
  if (!next[EXTRA_SLOTS_FIELD] || typeof next[EXTRA_SLOTS_FIELD] !== "object") {
    next[EXTRA_SLOTS_FIELD] = {};
  }
  if (!next[UNIT_TEXT_FIELD] || typeof next[UNIT_TEXT_FIELD] !== "object") {
    next[UNIT_TEXT_FIELD] = {};
  }
  if (!next[AVAILABLE_SHIFTS_FIELD] || typeof next[AVAILABLE_SHIFTS_FIELD] !== "object") {
    next[AVAILABLE_SHIFTS_FIELD] = {};
  }

    const allKnownRoleKeys = new Set();

  const seedRoleKey = (roleKey) => {
    allKnownRoleKeys.add(roleKey);

    if (isLegacyCartBikeBaseKey(roleKey)) {
      const { medicKey, partnerKey } = getCartBikeChildKeys(roleKey);
      allKnownRoleKeys.add(medicKey);
      allKnownRoleKeys.add(partnerKey);
    }

    if (isTentBaseKey(roleKey)) {
      getTentChildKeys(roleKey).forEach((childKey) => {
        allKnownRoleKeys.add(childKey);
      });
    }
  };

  for (const block of BLOCKS) {
    for (const roleKey of block.slots || []) {
      seedRoleKey(roleKey);
    }
  }

  for (const roleKey of FRONT_9_ASSIGNMENTS) {
    seedRoleKey(roleKey);
  }

  for (const roleKey of BACK_9_ASSIGNMENTS) {
    seedRoleKey(roleKey);
  }

  for (const [, extras] of Object.entries(next[EXTRA_SLOTS_FIELD] || {})) {
    if (!Array.isArray(extras)) continue;
    for (const extra of extras) {
      if (extra?.id) allKnownRoleKeys.add(extra.id);
    }
  }

  const legacyTimes = next.__times && typeof next.__times === "object" ? next.__times : {};

  for (const roleKey of Object.keys(next)) {
    if (roleKey.startsWith("__")) continue;
    allKnownRoleKeys.add(roleKey);
  }

  for (const roleKey of allKnownRoleKeys) {
    if (isAmbulanceSlot(roleKey)) {
      if (!Array.isArray(next[ASSIGNMENTS_FIELD][roleKey])) {
        next[ASSIGNMENTS_FIELD][roleKey] = [];
      }
      continue;
    }

    const cur = next[ASSIGNMENTS_FIELD][roleKey];
    if (Array.isArray(cur)) {
      next[ASSIGNMENTS_FIELD][roleKey] = cur
        .filter(Boolean)
        .map((a) => ({
          assignmentId: a.assignmentId || uid("asg"),
          employeeId: a.employeeId || a.id || a.empId || a,
          start: a.start || legacyTimes?.[roleKey]?.start || getRoleDefaultTimes(roleKey).start,
          end: a.end || legacyTimes?.[roleKey]?.end || getRoleDefaultTimes(roleKey).end,
        }))
        .filter((a) => a.employeeId && a.start && a.end);
      continue;
    }

    if (Array.isArray(next[roleKey])) {
      next[ASSIGNMENTS_FIELD][roleKey] = next[roleKey]
        .filter(Boolean)
        .map((employeeId) => ({
          assignmentId: uid("asg"),
          employeeId,
          start: legacyTimes?.[roleKey]?.start || getRoleDefaultTimes(roleKey).start,
          end: legacyTimes?.[roleKey]?.end || getRoleDefaultTimes(roleKey).end,
        }));
    } else if (!Array.isArray(next[ASSIGNMENTS_FIELD][roleKey])) {
      next[ASSIGNMENTS_FIELD][roleKey] = [];
    }

    next[ASSIGNMENTS_FIELD][roleKey] = sortAssignments(next[ASSIGNMENTS_FIELD][roleKey]).filter((a) => {
      const s = timeToMinutes(a.start);
      const e = timeToMinutes(a.end);
      return a.employeeId && s != null && e != null && s < e;
    });
  }

  /* -------- migrate legacy cart/bike single-key assignments -------- */
  for (const roleKey of [...allKnownRoleKeys]) {
    if (!isLegacyCartBikeBaseKey(roleKey)) continue;

    const legacyList = Array.isArray(next[ASSIGNMENTS_FIELD][roleKey])
      ? sortAssignments(next[ASSIGNMENTS_FIELD][roleKey])
      : [];

    const { medicKey, partnerKey } = getCartBikeChildKeys(roleKey);

    if (!Array.isArray(next[ASSIGNMENTS_FIELD][medicKey])) {
      next[ASSIGNMENTS_FIELD][medicKey] = [];
    }
    if (!Array.isArray(next[ASSIGNMENTS_FIELD][partnerKey])) {
      next[ASSIGNMENTS_FIELD][partnerKey] = [];
    }

    const childAlreadyHasData =
      next[ASSIGNMENTS_FIELD][medicKey].length > 0 ||
      next[ASSIGNMENTS_FIELD][partnerKey].length > 0;

    if (!childAlreadyHasData && legacyList.length > 0) {
      const usedIds = new Set();

      const paramedics = [];
      const nonParamedics = [];

      for (const a of legacyList) {
        const emp =
          (typeof window !== "undefined" &&
            window.__scheduleEmployeeById &&
            typeof window.__scheduleEmployeeById.get === "function")
            ? window.__scheduleEmployeeById.get(a.employeeId)
            : null;

        if (emp && isParamedic(emp)) {
          paramedics.push(a);
        } else {
          nonParamedics.push(a);
        }
      }

      if (paramedics.length > 0) {
        next[ASSIGNMENTS_FIELD][medicKey].push(paramedics[0]);
        usedIds.add(paramedics[0].assignmentId);
      } else if (legacyList.length > 0) {
        next[ASSIGNMENTS_FIELD][medicKey].push(legacyList[0]);
        usedIds.add(legacyList[0].assignmentId);
      }

      const partnerCandidate =
        nonParamedics.find((a) => !usedIds.has(a.assignmentId)) ||
        paramedics.find((a) => !usedIds.has(a.assignmentId)) ||
        legacyList.find((a) => !usedIds.has(a.assignmentId));

      if (partnerCandidate) {
        next[ASSIGNMENTS_FIELD][partnerKey].push(partnerCandidate);
        usedIds.add(partnerCandidate.assignmentId);
      }
    }

    next[ASSIGNMENTS_FIELD][medicKey] = sortAssignments(
      (next[ASSIGNMENTS_FIELD][medicKey] || []).filter((a) => {
        const s = timeToMinutes(a.start);
        const e = timeToMinutes(a.end);
        return a.employeeId && s != null && e != null && s < e;
      })
    );

    next[ASSIGNMENTS_FIELD][partnerKey] = sortAssignments(
      (next[ASSIGNMENTS_FIELD][partnerKey] || []).filter((a) => {
        const s = timeToMinutes(a.start);
        const e = timeToMinutes(a.end);
        return a.employeeId && s != null && e != null && s < e;
      })
    );

    next[ASSIGNMENTS_FIELD][roleKey] = [];
    next[roleKey] = [];
  }

  /* -------- migrate legacy tent single-key assignments -------- */
  for (const roleKey of [...allKnownRoleKeys]) {
    if (!isTentBaseKey(roleKey)) continue;

    const legacyList = Array.isArray(next[ASSIGNMENTS_FIELD][roleKey])
      ? sortAssignments(next[ASSIGNMENTS_FIELD][roleKey])
      : [];

    const childKeys = getTentChildKeys(roleKey);

    childKeys.forEach((childKey) => {
      if (!Array.isArray(next[ASSIGNMENTS_FIELD][childKey])) {
        next[ASSIGNMENTS_FIELD][childKey] = [];
      }
    });

    const childAlreadyHasData = childKeys.some(
      (childKey) => (next[ASSIGNMENTS_FIELD][childKey] || []).length > 0
    );

    if (!childAlreadyHasData && legacyList.length > 0) {
      legacyList.slice(0, 4).forEach((assignment, idx) => {
        next[ASSIGNMENTS_FIELD][childKeys[idx]].push(assignment);
      });
    }

    childKeys.forEach((childKey) => {
      next[ASSIGNMENTS_FIELD][childKey] = sortAssignments(
        (next[ASSIGNMENTS_FIELD][childKey] || []).filter((a) => {
          const s = timeToMinutes(a.start);
          const e = timeToMinutes(a.end);
          return a.employeeId && s != null && e != null && s < e;
        })
      );
    });

    next[ASSIGNMENTS_FIELD][roleKey] = [];
    next[roleKey] = [];
  }

  const availBag = {};
  for (const [roleKey, arr] of Object.entries(next[AVAILABLE_SHIFTS_FIELD] || {})) {
    availBag[roleKey] = Array.isArray(arr)
      ? arr.filter(Boolean).map((s) => ({
          id: s.id || uid("avail"),
          roleKey: s.roleKey || roleKey,
          roleLabel: s.roleLabel || roleKey,
          position: s.position || "",
          start: s.start || getRoleDefaultTimes(roleKey).start,
          end: s.end || getRoleDefaultTimes(roleKey).end,
          createdAt: s.createdAt || null,
          createdBy: s.createdBy || "",
        }))
      : [];
  }
  next[AVAILABLE_SHIFTS_FIELD] = availBag;

  /* -------- clean legacy available shifts for old cart/bike base keys -------- */
  for (const roleKey of Object.keys(next[AVAILABLE_SHIFTS_FIELD])) {
    if (!isLegacyCartBikeBaseKey(roleKey)) continue;

    const { medicKey, partnerKey } = getCartBikeChildKeys(roleKey);
    const existing = next[AVAILABLE_SHIFTS_FIELD][roleKey] || [];

    if (
      (!next[AVAILABLE_SHIFTS_FIELD][medicKey] || next[AVAILABLE_SHIFTS_FIELD][medicKey].length === 0) &&
      (!next[AVAILABLE_SHIFTS_FIELD][partnerKey] || next[AVAILABLE_SHIFTS_FIELD][partnerKey].length === 0) &&
      existing.length > 0
    ) {
      const template = getRoleDefaultTimes(roleKey);

      next[AVAILABLE_SHIFTS_FIELD][medicKey] = [
        {
          id: uid("avail"),
          roleKey: medicKey,
          roleLabel: "Paramedic",
          position: "Paramedic",
          start: template.start,
          end: template.end,
          createdAt: null,
          createdBy: "system",
        },
      ];

      next[AVAILABLE_SHIFTS_FIELD][partnerKey] = [
        {
          id: uid("avail"),
          roleKey: partnerKey,
          roleLabel: "Partner",
          position: "EMT/Paramedic",
          start: template.start,
          end: template.end,
          createdAt: null,
          createdBy: "system",
        },
      ];
    }

    next[AVAILABLE_SHIFTS_FIELD][roleKey] = [];
  }

  return next;
}

export function serializeDay(dayObj) {
  const day = normalizeDay(dayObj);
  const out = { ...day };
  const assignments = day[ASSIGNMENTS_FIELD] || {};
  const legacyTimes = { ...(day.__times || {}) };

  for (const [roleKey, list] of Object.entries(assignments)) {
    if (isAmbulanceSlot(roleKey)) {
      out[ASSIGNMENTS_FIELD][roleKey] = [];
      out[roleKey] = [];
      if (!legacyTimes[roleKey]) {
        const tt = getRoleDefaultTimes(roleKey);
        legacyTimes[roleKey] = { start: tt.start, end: tt.end };
      }
      continue;
    }

    const cleaned = sortAssignments(list).filter((a) => {
      const s = timeToMinutes(a.start);
      const e = timeToMinutes(a.end);
      return a.employeeId && s != null && e != null && s < e;
    });

    out[ASSIGNMENTS_FIELD][roleKey] = cleaned;

    if (isLegacyCartBikeBaseKey(roleKey) || isTentBaseKey(roleKey)) {
      out[roleKey] = [];
    } else {
      out[roleKey] = cleaned.map((a) => a.employeeId);
    }

    if (!legacyTimes[roleKey]) {
      const tt = getRoleDefaultTimes(roleKey);
      const timeRole = isCartBikeChildKey(roleKey) ? getBaseRoleKeyFromChild(roleKey) : roleKey;
      const defaultTimes = getRoleDefaultTimes(timeRole || tt);
      legacyTimes[roleKey] = { start: defaultTimes.start, end: defaultTimes.end };
    }
  }

  out.__times = legacyTimes;
  return out;
}

export function makePublishedShift(roleKey, roleLabel, position, start, end, auto = false) {
  return {
    id: `auto_${roleKey}_${position}_${start}_${end}`,
    roleKey,
    roleLabel,
    position,
    start,
    end,
    auto,
    createdAt: null,
    createdBy: auto ? "system" : "admin",
  };
}

export function shouldAutoPublishRole(roleLabel) {
  if (!roleLabel) return false;
  if (isAmbulanceSlot(roleLabel)) return false;
  return true;
}

export function getAutoPublishedShiftsForSlot({ roleKey, roleLabel, blocked, assignments }) {
  if (blocked) return [];
  if (!shouldAutoPublishRole(roleLabel)) return [];
  if (isAmbulanceSlot(roleLabel)) return [];

  const baseRoleForTiming = isCartBikeChildKey(roleKey)
    ? getBaseRoleKeyFromChild(roleKey)
    : roleLabel;

  const template = getRoleDefaultTimes(baseRoleForTiming);
  const filled = assignments || [];

  if (roleLabel === "Dispatcher" || roleLabel === "Dispatcher 2") {
    return filled.length === 0
      ? [makePublishedShift(roleKey, roleLabel, "Dispatcher", template.start, template.end, true)]
      : [];
  }

  if (isCartBikeChildKey(roleKey)) {
    const childType = String(roleKey).toLowerCase().endsWith("__paramedic")
      ? "paramedic"
      : "partner";

    if (filled.length > 0) return [];

    if (childType === "paramedic") {
      return [makePublishedShift(roleKey, roleLabel, "Paramedic", template.start, template.end, true)];
    }

    return [makePublishedShift(roleKey, roleLabel, "EMT/Paramedic", template.start, template.end, true)];
  }

  if (isCartSlot(roleLabel) || isBikeSlot(roleLabel)) {
    const active = sortAssignments(filled);

    if (active.length === 0) {
      return [
        makePublishedShift(roleKey, roleLabel, "Paramedic", template.start, template.end, true),
        makePublishedShift(roleKey, roleLabel, "EMT/Paramedic", template.start, template.end, true),
      ];
    }

    if (active.length >= 2) return [];

    const firstEmp = active[0]?.employee;

    if (!firstEmp) {
      return [makePublishedShift(roleKey, roleLabel, "Paramedic", template.start, template.end, true)];
    }

    if (isParamedic(firstEmp)) {
      return [makePublishedShift(roleKey, roleLabel, "EMT/Paramedic", template.start, template.end, true)];
    }

    return [makePublishedShift(roleKey, roleLabel, "Paramedic", template.start, template.end, true)];
  }

  if (/^Tent\s+\d+/i.test(String(roleLabel || "").trim())) {
  // Each tent slot is its own roleKey (Tent 3__1, __2, etc)
  if (filled.length === 0) {
    return [
      makePublishedShift(
        roleKey,
        roleLabel,
        "EMT/Paramedic",
        template.start,
        template.end,
        true
      ),
    ];
  }
  return [];
}

  return filled.length === 0
    ? [makePublishedShift(roleKey, roleLabel, roleLabel, template.start, template.end, true)]
    : [];
}